import { scoreColor } from "../lib/format";
import { dictionaries } from "../lib/i18n";
import type { UiLang } from "../lib/types";

interface Props {
  score: number;
  label: string;
  size?: number;
  lang: UiLang;
  /**
   * Hva tallet er regnet av.
   *
   * `normals` er klimanormalen for måneden, brukt der ingen modell rekker
   * fram. `extended` er langtidsvarselet for døgn 10–16 — et ekte varsel, men
   * grovere og mindre sikkert. Begge skal *stå på måleren* og ikke bare i
   * teksten under: et anslag på 58 og et varsel på 58 er ikke samme påstand,
   * og en måler man må lese forklaringen til for å tolke, blir tolket feil.
   *
   * De to deler den stiplede ringen, men ikke merkelappen. Formen sier «ikke
   * det sikreste tallet», som er sant om begge; ordet sier hvilket av dem, og
   * det avledes av `caption` under. Se api/src/services/extended.ts.
   */
  basis?: "forecast" | "extended" | "normals" | "none";
}

/**
 * Ringmåler for utsiktsindeksen — «hvor mye av det du kom for kommer du
 * faktisk til å se?». Fargen går fra rust (skjult) til mose (klart).
 */
export function ViewScoreDial({ score, label, size = 46, basis, lang }: Props) {
  const t = dictionaries[lang];
  const estimated = basis === "normals" || basis === "extended";

  /*
   * Teksten under tallet, utledet av grunnlaget framfor sendt inn.
   *
   * Sto før som `estimateLabel` hos kalleren, med «Utsiktsindeks» hardkodet på
   * norsk som reserve — så en fransk bruker leste «Utsiktsindeks» under måleren
   * på hver dag. Oversettelsen fantes hele tiden i `viewIndex`, i alle fem
   * ordbøkene; det var koblingen som manglet.
   *
   * At avledningen bor her og ikke hos kalleren er ikke bare kortere: hvilket
   * ord som hører til hvilket grunnlag er et faktum om grunnlaget, ikke en
   * avgjørelse hver kaller skal ta på nytt — og to kallesteder som tar den
   * ulikt er to ulike påstander om samme tall.
   */
  const caption =
    basis === "extended"
      ? t.extendedDialLabel
      : basis === "normals"
        ? t.outlookEstimate
        : t.viewIndex;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.max(0, Math.min(100, score)) / 100) * circumference;
  const color = scoreColor(score);

  return (
    <div className={estimated ? "dial dial--estimate" : "dial"}>
      {/*
        Skjermleseren fikk «Utsiktsindeks 71 av 100» uansett språk. Tallet
        skrives nå som brøk framfor med et bindeord, slik at setningen ikke
        trenger en egen nøkkel i fem ordbøker for å være riktig.
      */}
      <svg width={size} height={size} role="img" aria-label={`${t.viewIndex} ${score}/100`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.18}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          /*
           * Stiplet ring når tallet er et anslag. Formen bærer forskjellen i
           * tillegg til teksten, så den leses også av den som bare skummer —
           * og av den som ikke skiller fargene.
           */
          strokeDasharray={
            estimated ? `${Math.max(filled / 6, 2)} 3` : `${filled} ${circumference - filled}`
          }
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        {/*
          Tallet står hvitt, ikke i poengfargen.

          Ringen over bærer allerede fargekodingen, og den er en strek — der
          holder 3:1. Tallet er skrift, og krever 4,5:1. Målt på tavla, som er
          mørk i begge temaer: `#4F7A5B` er 2,9:1 der, og den dempes videre til
          0,85 når varselet er et anslag. Fargen sa altså det ringen alt sa, og
          betalte for det med lesbarheten til det ene tallet folk ser etter.
        */}
        <text
          x={size / 2}
          y={size / 2 + 4}
          textAnchor="middle"
          fontFamily="'IBM Plex Mono', monospace"
          fontSize={13}
          fontWeight={700}
          style={{ fill: "var(--on-dark)" }}
        >
          {score}
        </text>
      </svg>
      <div className="dial-text">
        <b>{label}</b>
        <span>{caption}</span>
      </div>
    </div>
  );
}
